import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { api, formatApiErrorDetail } from "./api";

const AuthCtx = createContext({});

export const ROLE_LABELS = {
  admin: "Admin",
  accounts: "Accounts",
  hk_supervisor: "Housekeeping Supervisor",
  hk_staff: "Housekeeping Staff",
  laundry: "Laundry Dispatch/Receiving",
  front_office: "Front Office",
  maintenance: "Maintenance",
};

const MODULE_LEVELS = { none: 0, view: 1, manage: 2 };
const OPERATIONAL_APPROVER_ROLES = new Set([
  "admin",
  "accounts",
  "hk_supervisor",
]);
const MODULE_DEFAULTS = {
  admin: {
    dashboard: "manage",
    master_linen_stock: "manage",
    store_inventory: "manage",
    purchase_register: "manage",
    asset_management: "manage",
    audit_log: "manage",
  },
  accounts: {
    dashboard: "manage",
    master_linen_stock: "manage",
    store_inventory: "manage",
    purchase_register: "manage",
    asset_management: "manage",
    audit_log: "view",
  },
  hk_supervisor: {
    dashboard: "manage",
    master_linen_stock: "manage",
    store_inventory: "manage",
    purchase_register: "manage",
    asset_management: "manage",
    audit_log: "view",
  },
  hk_staff: { store_inventory: "manage" },
  laundry: { store_inventory: "manage" },
  front_office: {
    dashboard: "manage",
    store_inventory: "manage",
    purchase_register: "manage",
    asset_management: "view",
  },
  maintenance: { asset_management: "view" },
};

// Default landing route by role (used post-login and by any "not allowed" fallback).
export function defaultRouteFor(role) {
  switch (role) {
    case "admin":
    case "accounts":
    case "hk_supervisor":
      return "/dashboard";
    case "hk_staff":
      return "/housekeeping";
    case "laundry":
      return "/laundry";
    case "front_office":
      return "/rooms";
    case "maintenance":
      return "/assets/maintenance";
    default:
      return "/login";
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Not authenticated (401) is expected on first visit — treat as anon and log unexpected errors.
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("Auth me check failed:", e?.response?.status, e?.message);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Auth tokens live in httpOnly cookies set by the server; we deliberately do NOT
      // store the access_token in localStorage to avoid XSS token theft.
      setUser(data);
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "account_locked" && detail?.locked_until) {
        setError(detail);
      } else {
        setError(formatApiErrorDetail(detail) || e.message);
      }
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn(
        "Logout call failed (continuing to clear session):",
        e?.message,
      );
    }
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, error, login, logout, refresh: fetchMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function hasRole(user, ...roles) {
  if (!user || !user.role) return false;
  return roles.includes(user.role);
}

export function canOperationalApprove(user) {
  return !!user && OPERATIONAL_APPROVER_ROLES.has(user.role);
}

export function hasModuleAccess(user, module, minimum = "view") {
  if (!user || !MODULE_LEVELS[minimum]) return false;
  const defaults = MODULE_DEFAULTS[user.role] || {};
  const effective =
    user.effective_permissions?.[module] || defaults[module] || "none";
  return MODULE_LEVELS[effective] >= MODULE_LEVELS[minimum];
}
